import React, { useEffect, useRef,useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Stack,
} from "@mui/material";
import { styled } from "@mui/system";
import { getDataPlanCode } from "../Payment/PaymentService/paymentService";

const ScrollableChannelPay = ({
  policies,
  selectedPolicyNos, // ['0032...', ...]
  onTogglePolicy, // (policyNo, checked) => void
  selections, // { [policyNo]: 'credit' | 'counter' | 'account' }
  onSelectPaymentForPolicy, // (policyNo, value) => void
  getAvailableOptions, // (currentLabel) => [{value,label}]
  ensureCreditStatus, // (policyNo) => Promise<void>
  creditMap, // { [policyNo]: boolean }  true = ยังไม่ผูกบัตร -> ปิด credit
}) => {
  const cardRef = useRef(null);
  const [allowedBaseCodes, setAllowedBaseCodes] = useState(null); // null = ยังโหลดไม่เสร็จ

  useEffect(() => {
    if (policies?.length > 0) {
      getDataPlanCodeApi();
    }
    policies?.forEach(
      (p) => ensureCreditStatus && ensureCreditStatus(p.policyNo)
    );
  }, [policies]);

  const CustomCheckbox = styled(Checkbox)({
    color: "rgba(255, 94, 67, 0.5)",
    "&.Mui-checked": { color: "#FF5E43" },
    marginRight: "15px",
  });

  const isChecked = (policyNo) => selectedPolicyNos?.includes(policyNo);

  const getDataPlanCodeApi = async () => {
    const results = await Promise.all(
      policies.map((p) => getDataPlanCode(p.baseCode))
    );
    const allowed = new Set(
      policies
        .filter((p, i) => (results[i]?.data?.length ?? 0) > 0)
        .map((p) => p.baseCode)
    );
    setAllowedBaseCodes(allowed);
  };

  return (
    <Card
      ref={cardRef}
      sx={{
        maxHeight: { xs: "450px", sm: "490px" },
        overflowY: "auto",
        borderRadius: "12px",
        border: "1px solid rgba(255, 94, 67, 0.3)",
        borderWidth: "2px",
        boxShadow: "none",
        width: "100%",
        margin: "0 auto",
      }}
    >
      <CardContent sx={{ padding: { xs: "8px", sm: "16px" } }}>
        {policies && policies.length > 0 ? (
          policies.map((policy) => {
            const policyNo = policy.policyNo;
            const available = getAvailableOptions(policy.policyPaymentMethod);
            const creditBlocked = !!creditMap[policyNo]; // true = ยังไม่ผูกบัตร -> ปิด credit
            const selectedValue = selections[policyNo] || "";

            // policy นี้ไม่มี baseCode ใน config → disable ทั้งการ์ด
            const isPlanDisabled = allowedBaseCodes !== null && !allowedBaseCodes.has(policy.baseCode);

            // ถ้า plan ไม่ disable ให้เช็ค usable option ตามปกติ
            const hasUsableOption = !isPlanDisabled && available.some((opt) => {
              if (opt.value === "credit") return !creditBlocked;
              return true;
            });

            const disablePolicyCheckbox = isPlanDisabled || !hasUsableOption;

            return (
              <Box
                key={policyNo}
                sx={{
                  mb: { xs: "10px", sm: "15px" },
                  p: { xs: "6px", sm: "10px" },
                  borderRadius: "12px",
                  cursor: disablePolicyCheckbox ? "not-allowed" : "default",
                }}
              >
                <Card
                  sx={{
                    backgroundColor: isChecked(policyNo)
                      ? "rgba(255, 94, 67, 0.1)"
                      : "transparent",
                    transition: "background-color 0.3s ease",
                    border: "1px solid rgba(255, 94, 67, 0.3)",
                    borderRadius: "12px",
                    borderWidth: "2px",
                    boxShadow: "none",
                  }}
                >
                  <CardHeader
                    title={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <CustomCheckbox
                          checked={isChecked(policyNo)}
                          disabled={disablePolicyCheckbox}
                          onChange={(e) => {
                            if (disablePolicyCheckbox) return;
                            onTogglePolicy(policyNo, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ padding: "4px", flexShrink: 0, opacity: disablePolicyCheckbox ? 0.6 : 1 }}
                        />
                        <Stack
                          spacing={1}
                          sx={{ width: "100%", overflow: "hidden" }}
                        >
                          <Box
                            sx={{
                              ml: "10px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: { xs: "14px", sm: "15px" },
                              fontFamily: "Prompt-Thin",
                              fontWeight: "bold",
                            }}
                          >
                            {"กรมธรรม์เลขที่ : " + policyNo}
                          </Box>
                          <Box
                            sx={{
                              ml: "10px",
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              wordBreak: "break-word",
                              lineHeight: 1.4,
                              fontSize: { xs: "13px", sm: "14px" },
                              fontFamily: "Prompt-Thin",
                              fontWeight: "bold",
                            }}
                          >
                            {"แบบประกัน : " + (policy.baseName || "")}
                          </Box>
                        </Stack>
                      </Box>
                    }
                    sx={{
                      backgroundColor: "rgba(255, 94, 67, 0.3)",
                      p: { xs: "8px", sm: "16px" },
                    }}
                  />

                  <CardContent sx={{ p: { xs: "12px", sm: "16px" } }}>
                    {/* ช่องทางปัจจุบัน */}
                    <Typography
                      variant="body2"
                      sx={{
                        display: "flex",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        alignItems: "center",
                        mb: 1,
                        p: { xs: "4px", sm: "8px" },
                        fontFamily: "Prompt-Thin",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          minWidth: { xs: "120px", sm: "160px" },
                          mr: 1,
                          color: "#555",
                        }}
                      >
                        ช่องทางการชำระปัจจุบัน :
                      </Box>
                      <Box
                        component="span"
                        sx={{ fontWeight: "bold", color: "#333" }}
                      >
                        {policy.policyPaymentMethod}
                      </Box>
                    </Typography>
                    <Typography
                      sx={{ fontWeight: "bold", mb: 1, marginLeft: "10px" }}
                    >
                      เปลี่ยนช่องทางการชำระเป็น
                    </Typography>

                    {/* ตัวเลือกช่องทางที่ต้องการเปลี่ยน */}
                    <Box sx={{ mt: 1, marginLeft: "50px" }}>
                      {isPlanDisabled ? (
                        <Typography
                          variant="body2"
                          sx={{ color: "red", fontFamily: "Prompt-Thin" }}
                        >
                          *แบบประกันของท่าน ไม่รองรับการชำระเบี้ยผ่านบัตรเครดิตอัตโนมัติ กรุณา ติดต่อฝ่ายลูกค้าสัมพันธ์
                        </Typography>
                      ) : (
                        <RadioGroup
                          value={selectedValue}
                          onChange={(e) =>
                            onSelectPaymentForPolicy(policyNo, e.target.value)
                          }
                        >
                          {available.map((opt) => {
                            const optionDisabled =
                              opt.value === "credit" && creditBlocked;
                            return (
                              <Box key={opt.value} sx={{ mb: 1 }}>
                                <FormControlLabel
                                  value={opt.value}
                                  control={<Radio disabled={optionDisabled} sx={{ opacity: optionDisabled ? 0.6 : 1 }} />}
                                  label={opt.label}
                                  disabled={optionDisabled}
                                  sx={{
                                    fontFamily: "Prompt-Regular",
                                    opacity: optionDisabled ? 0.6 : 1,
                                    color: "text.primary",
                                  }}
                                />

                                {policy.policyPaymentMethod === "ชำระผ่านเคาน์เตอร์ธนาคาร" &&
                                 policy.policyStatus === "มีผลบังคับ" &&
                                 opt.value === "credit" && (
                                  <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ fontFamily: "Prompt-Thin", ml: 4 }}
                                  >
                                    เพื่อดำเนินการต่อ
                                    กรุณาผูกบัตรเครดิตกับกรมธรรม์ของท่าน{" "}
                                    <a
                                      href="/personal/card"
                                      style={{
                                        color: "#1976d2",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                      }}
                                    >
                                      คลิกที่นี่
                                    </a>
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </RadioGroup>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })
        ) : (
          <Box sx={{ textAlign: "center", p: 2 }}>
            <Typography
              variant="h6"
              sx={{ color: "#666", fontFamily: '"Prompt-Thin", sans-serif' }}
            >
              ไม่พบข้อมูล
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ScrollableChannelPay;
